/**
 * @file components/common/ClientSelect.tsx
 * @description 全系統共用「客戶名稱」下拉選擇器。
 *
 * 用法 1（推薦，主鍵存 clientId）：
 * ```tsx
 * <Form.Item label="客戶名稱" name="clientId" rules={[{ required: true, message: '請選擇客戶' }]}>
 *   <ClientSelect nameField="clientName" />
 * </Form.Item>
 * <Form.Item name="clientName" hidden><Input /></Form.Item>
 * ```
 * - 主字段 value = clientId (number)
 * - 選中後自動把 client.name 寫入 form 的 `clientName` 隱藏字段，方便列表顯示與後端兼容。
 *
 * 用法 2（純展示，存 clientName 字串）：
 * ```tsx
 * <Form.Item name="clientName"><ClientSelect valueField="name" /></Form.Item>
 * ```
 *
 * 數據來源：useClients hook（共享 TanStack Query 快取）。
 */

import { useMemo } from 'react';
import { Form, Select, type SelectProps } from 'antd';
import { useClients, type ClientItem } from '@/hooks/useClients';

/** ClientSelect Props（在 antd Select 基礎上擴充） */
export interface ClientSelectProps extends Omit<
  SelectProps,
  'options' | 'loading' | 'showSearch' | 'filterOption' | 'mode'
> {
  /**
   * 選中後同步把客戶名稱（client.name）寫入 form 的此字段。
   * 通常配合一個 hidden 的 `<Form.Item name={nameField}>` 使用，
   * 用於保留 clientName 字串給列表/詳情顯示，或符合後端既有契約。
   */
  nameField?: string;
  /**
   * 主字段使用的取值字段：
   *   - 'id'（預設）：value = clientId (number)
   *   - 'name'：value = clientName (string)，用於只想存名稱的舊頁面
   */
  valueField?: 'id' | 'name';
}

/**
 * 客戶下拉選擇器。
 */
export default function ClientSelect({
  nameField,
  valueField = 'id',
  onChange,
  placeholder = '請搜尋並選擇客戶',
  ...rest
}: ClientSelectProps) {
  const form = Form.useFormInstance();
  const { data, isLoading } = useClients();

  /** 後端返回的客戶列表 */
  const clients: ClientItem[] = useMemo(() => data?.items ?? [], [data]);

  /** 轉換為 Select options，value 由 valueField 決定 */
  const options = useMemo(
    () =>
      clients.map((c) => ({
        value: valueField === 'id' ? c.id : c.name,
        label: c.name,
      })),
    [clients, valueField],
  );

  return (
    <Select
      placeholder={placeholder}
      showSearch
      loading={isLoading}
      // 用 label 作為搜尋目標
      optionFilterProp="label"
      filterOption={(input, option) =>
        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
      }
      options={options}
      onChange={(value, option) => {
        // 同步寫入 nameField（如 clientName）
        if (nameField && form) {
          const matched =
            valueField === 'id'
              ? clients.find((c) => c.id === value)
              : clients.find((c) => c.name === value);
          form.setFieldValue(nameField, matched?.name ?? '');
        }
        onChange?.(value, option);
      }}
      {...rest}
    />
  );
}
